import React, { useState, useEffect, useMemo } from 'react';
import { useBackgroundPreloader } from '../hooks/useImagePreloader';
import { getDailyBackground } from '../config/backgroundsConfig';

/**
 * Background Image Component with smooth loading transition
 * Handles the progressive loading issue for large background images.
 * 
 * If no src is provided, automatically uses the daily rotating background.
 */
const BackgroundImage = ({ 
  src, 
  alt = '', 
  className = 'bg-img', 
  fallbackColor = '#e9ebe8',
  blurAmount = 20,
  ...props 
}) => {
  // Use provided src or fall back to daily background
  // useMemo ensures the same background is used throughout the component lifecycle
  const backgroundSrc = useMemo(() => src || getDailyBackground(), [src]);
  
  const [showImage, setShowImage] = useState(false);
  const { isLoaded, isLoading, wasCached } = useBackgroundPreloader(backgroundSrc);

  // Handle image display based on loading state
  useEffect(() => {
    if (isLoaded) {
      if (wasCached) {
        // Image is cached - show immediately with no animation
        setShowImage(true);
      } else {
        // First load - use smooth transition with small delay
        const timeout = setTimeout(() => {
          setShowImage(true);
        }, 100);
        
        return () => clearTimeout(timeout);
      }
    } else {
      // Not loaded yet
      setShowImage(false);
    }
  }, [isLoaded, wasCached]);

  // Skip transitions for cached images to avoid flash
  const shouldAnimate = !wasCached;

  return (
    <>
      {/* Fallback background while loading - hide immediately if cached */}
      <div
        className={`${className}-fallback`}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          backgroundColor: fallbackColor,
          background: `linear-gradient(135deg, ${fallbackColor} 0%, #d3d5d0 100%)`,
          zIndex: -3,
          opacity: showImage ? 0 : 1,
          transition: shouldAnimate ? 'opacity 0.5s ease-out' : 'none',
          display: wasCached ? 'none' : 'block', // Hide completely if cached
        }}
      />

      {/* Blurred version that appears first - only show for first load */}
      {isLoading && !wasCached && (
        <div
          className={`${className}-blur`}
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${backgroundSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: `blur(${blurAmount}px) brightness(0.8)`,
            transform: 'scale(1.1)', // Slightly larger to hide blur edges
            zIndex: -2,
            opacity: isLoaded ? 0 : 0.6,
            transition: 'opacity 0.3s ease-out, filter 0.3s ease-out',
          }}
        />
      )}

      {/* Main background image - dimmed so it doesn't distract from content */}
      <img
        src={backgroundSrc}
        alt={alt}
        className={className}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -2,
          opacity: showImage ? 1 : 0,
          filter: showImage
            ? 'brightness(0.8) saturate(0.9)'
            : (shouldAnimate ? 'blur(1px) brightness(0.8) saturate(0.9)' : 'brightness(0.8) saturate(0.9)'),
          transition: shouldAnimate ? 'opacity 0.8s ease-out, filter 0.8s ease-out' : 'none',
        }}
        loading="lazy" // Defer background image load to reduce initial bandwidth
        fetchpriority="low" // Low priority to yield bandwidth to content
        {...props}
      />

      {/* Loading indicator (optional) */}
      {isLoading && !showImage && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '14px',
            fontWeight: '500',
            background: 'rgba(0, 0, 0, 0.1)',
            padding: '12px 20px',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            opacity: isLoading ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
          }}
        >
          Загрузка...
        </div>
      )}
    </>
  );
};

export default BackgroundImage;
